import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Chip,
  Grid,
  IconButton,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { useSelector } from "react-redux";
import axios from "axios";
import ReusableDialog from "../../../../common/dialog/dialog";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

const ManagerFirstAidChecklist = ({ onClose, setOpenDefaultSnackbar }) => {
  const [firstAids, setFirstAids] = useState([
    "Guidance Card",
    "Plasters",
    "Sterile eye Pads",
    "Triangular Bandage",
    "Safety Pins",
    "Large Wound Dressing",
    "Medium Wound Dressing",
    "Disposable Gloves",
  ]);
  const user = useSelector((state) => state.auth.user); // User data from Redux store
  const today = new Date();
  const currentDate = today.toISOString().split("T")[0];
  const [quantity, setQuantity] = useState(1);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [responses, setResponses] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [newItemCount, setNewItemCount] = useState(0);

  const handleDialogClose = () => {
    setDialogOpen(false); // Close the dialog
  };

  const handleSubmitItem = () => {
    setFirstAids([...firstAids, newItemName]);
    setDialogOpen(false);
  };

  const handleAddItem = () => {
    setDialogOpen(true); // Open the dialog
  };

  const handleNext = () => {
    setResponses([
      ...responses,
      {
        firstAid: firstAids[currentProductIndex],
        quantity,
        date: selectedDate,
      },
    ]);

    if (currentProductIndex < firstAids.length - 1) {
      setCurrentProductIndex((prev) => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    const payload = {
      firstAids: responses,
      location: selectedLocation,
      created_by: user.username,
    };

    try {
      // Proceed with submission if no duplicates found
      await axios.post("/api/firstAid/firstAidItem", payload, {
        headers: { "Content-Type": "application/json", "x-user-role": user?.roleid },
      });
      setResponses([]);
      setQuantity(1);
      setSelectedDate(currentDate);
      onClose();
      setOpenDefaultSnackbar(true);
    } catch (error) {
      if (error.response && error.response.status === 409) {
        alert("A first aid item with the location already exists.");
      }
    }
  };

  return (
    <Box sx={{ p: 4, m: 2 }}>
      <Chip
        variant='contained'
        sx={{ fontSize: "14px", backgroundColor: "#F2F6FF", color: "#1751D0" }}
        label='Weekly Tasks'
      />

      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold", mb: 1 }}>
        Weekly First Aid Item Check List
      </Typography>

      <Typography variant='subtitle2' sx={{ color: "#757575" }}>
        Date: {currentDate}
      </Typography>

      <Grid item xs={12}>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant='contained' color='primary' onClick={handleAddItem}>
            Add Item
          </Button>
        </Box>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 5 }}>
        <Grid item sm={2}>
          <FormControl fullWidth sx={{ height: "10px" }}>
            <InputLabel sx={{ fontSize: "14px" }}>Location</InputLabel>
            <Select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              displayEmpty
              size='small'
            >
              <MenuItem value='Location 1'>Location 1</MenuItem>
              <MenuItem value='Location 2'>Location 2</MenuItem>
              <MenuItem value='Location 3'>Location 3</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Typography variant='subtitle2' sx={{ color: "#757575", fontSize: "14px", mb: 2 }}>
            <Grid container>
              <Grid item xs={11}>
                Items
              </Grid>
              <Grid item xs={1}>
                <span style={{ color: "#1751D0" }}>{currentProductIndex + 1} </span>
                {`/ ${firstAids.length}`}
              </Grid>
            </Grid>
          </Typography>
          {firstAids.map((firstAid, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #E0E0E0",
                py: 1,
              }}
            >
              <Typography sx={{ flex: 1, color: "black", fontSize: "14px" }}>{`${index + 1} ${firstAid}`}</Typography>
              <IconButton
                color='primary'
                sx={{
                  color: responses.find((response) => response.firstAid === firstAid) ? "#1751D0" : "#E0E0E0",
                }}
              >
                <CheckCircleIcon />
              </IconButton>
            </Box>
          ))}
          <Typography variant='body2' sx={{ color: "#757575", mt: 3 }}>
            All staff are to ensure there is the correct number of items always present within the home. Any items that
            are missing are to be looked for. Any concerns please inform the duty manager. Any damaged items or items
            that have been replaced; the home manager is to be informed.
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Grid container>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: "14px", mb: 1 }}>Item</Typography>
              <Typography sx={{ fontSize: "14px", mb: 2, color: "black" }}>{firstAids[currentProductIndex]}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant='outlined'
                startIcon={<ShoppingCartOutlinedIcon />}
                sx={{
                  borderRadius: "50px", // Rounded edges
                  borderColor: "#2979ff", // Border color (blue)
                  color: "#4a4a4a", // Text color
                  fontWeight: "50",
                  textTransform: "none", // Prevent uppercase text
                  padding: "8px 20px",
                  fontSize: "12px",
                  "&:hover": {
                    borderColor: "#1a73e8",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Add to shopping list
              </Button>
            </Grid>
          </Grid>

          <Typography sx={{ fontSize: "14px", mb: 1 }}>Quantity</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Button variant='outlined' onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}>
              -
            </Button>
            <Typography>{quantity}</Typography>
            <Button variant='outlined' onClick={() => setQuantity((prev) => prev + 1)}>
              +
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
        {currentProductIndex === 0 && (
          <Button variant='outlined' color='primary' onClick={() => onClose()}>
            Cancel
          </Button>
        )}
        {currentProductIndex > 0 && (
          <Button variant='outlined' color='primary' onClick={() => setCurrentProductIndex((prev) => prev - 1)}>
            Previous
          </Button>
        )}
        {currentProductIndex < firstAids.length - 1 ? (
          <Button variant='contained' color='primary' onClick={handleNext} disabled={quantity === 0}>
            Next
          </Button>
        ) : (
          <Button variant='contained' color='primary' onClick={handleSubmit} disabled={quantity === 0}>
            Submit
          </Button>
        )}
      </Box>

      <ReusableDialog
        isOpen={dialogOpen}
        onClose={handleDialogClose}
        title='Add an Item'
        onSubmit={handleSubmitItem}
        submitText='Submit'
        cancelText='Cancel'
        maxWidth='md'
        disable={!newItemName || newItemCount <= 0 || !selectedLocation}
      >
        <Typography sx={{ fontSize: "14px", color: "black" }}>Item Name</Typography>
        <TextField
          name='itemName'
          fullWidth
          variant='outlined'
          value={newItemName}
          onChange={(e) => setNewItemName(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography sx={{ fontSize: "14px", color: "black", mb: 1 }}>Count Available</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <TextField
                name='itemCount'
                value={newItemCount}
                size='small'
                type='number'
                inputProps={{ style: { textAlign: "center", width: "60px" } }}
                onChange={(e) => setNewItemCount(Number(e.target.value))}
              />
              <IconButton color='primary' onClick={() => setNewItemCount((prev) => Math.max(prev - 1, 0))}>
                <RemoveIcon />
              </IconButton>
              <IconButton color='primary' onClick={() => setNewItemCount((prev) => prev + 1)}>
                <AddIcon />
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              id='location'
              options={["Location 1", "Location 2", "Location 3"]}
              value={selectedLocation}
              onChange={(event, newValue) => setSelectedLocation(newValue)}
              renderInput={(params) => (
                <>
                  <Typography sx={{ fontSize: "14px", color: "black", mb: 1 }} align='left'>
                    Select the Location
                  </Typography>
                  <TextField {...params} placeholder='Location' />
                </>
              )}
              fullWidth
            />
          </Grid>
        </Grid>
      </ReusableDialog>
    </Box>
  );
};

export default ManagerFirstAidChecklist;
